// gh#351: shared shape and presentation rules for the CloudWatch alarm surface.
//
// Every alarm in the DeathStarApi stack has empty AlarmActions and there is no
// SNS topic, so a state change reaches nobody. The portal is the destination.
// Both consumers — the always-mounted AlarmBanner and the /alarms page — read
// the same endpoint and the same rules from here.

export type AlarmState = 'ALARM' | 'INSUFFICIENT_DATA' | 'OK';

export interface AlarmRecord {
  name: string;
  state: AlarmState;
  description: string | null;
  state_reason: string | null;
  state_updated_at: string | null;
  console_url: string;
}

export interface AlarmsResponse {
  alarms: AlarmRecord[];
  in_alarm_count: number;
  insufficient_data_count: number;
  truncated: boolean;
  dropped: number;
}

/**
 * True when the endpoint told us the listing is incomplete — the pagination cap
 * cut it short, or entries were unusable.
 *
 * This exists so `in_alarm_count === 0` is never read as "all clear" on a
 * listing that is admittedly partial: a firing alarm could be in the part we
 * did not get. Both consumers derive it from here so they cannot disagree about
 * what counts as complete.
 */
export function isPartialListing(data: AlarmsResponse): boolean {
  return data.truncated || data.dropped > 0;
}

export const ALARMS_PATH = '/api/alarms';

/**
 * Five minutes. The alarms behind this endpoint evaluate on 5-minute (DLQ depth)
 * to daily (blocking share, slicing pressure) periods, so polling faster would
 * only spend DescribeAlarms calls to re-read the same state. Slower would let a
 * long-lived tab sit on a stale all-clear.
 */
export const ALARMS_REFETCH_MS = 300_000;

interface StatePresentation {
  label: string;
  background: string;
  color: string;
  border: string;
}

const PRESENTATION: Record<AlarmState, StatePresentation> = {
  ALARM: {
    label: 'ALARM',
    background: 'var(--loop-red-100)',
    color: 'var(--loop-red-700)',
    border: 'var(--loop-red-200)',
  },
  // Amber, not grey. For CouncilBlockingSharePublisherSilentAlarm and
  // SlicingDiscoveryScanPressureAlarm, "no data" is the state that makes the
  // alarm above it blind — it is a warning, not an absence.
  INSUFFICIENT_DATA: {
    label: 'NO DATA',
    background: 'var(--loop-yellow-100)',
    color: 'var(--loop-yellow-800)',
    border: 'var(--loop-yellow-100)',
  },
  OK: {
    label: 'OK',
    background: 'var(--loop-green-100)',
    color: 'var(--loop-green-700)',
    border: 'var(--loop-green-200)',
  },
};

export function statePresentation(state: AlarmState): StatePresentation {
  // An unrecognised state from a future API version reads as NO DATA rather than
  // crashing or, worse, rendering as healthy.
  return PRESENTATION[state] ?? PRESENTATION.INSUFFICIENT_DATA;
}

/**
 * Fixed UTC rendering rather than toLocaleString(): these timestamps get quoted
 * into Slack and compared against CloudWatch, which shows UTC. An unparseable
 * value is echoed rather than rendered as "Invalid Date".
 */
export function formatChangedAt(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/** "3 alarms firing" / "1 alarm firing" — the banner headline. */
export function firingHeadline(count: number): string {
  return `${count} ${count === 1 ? 'alarm' : 'alarms'} firing`;
}
